import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Send, Mail, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnviarDocumentosSection = ({ dossierId }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-documentos-res022/${dossierId}/status`);
      setStatus(res.data);
    } catch {
      toast.error("No se pudo comprobar el estado de los documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!status?.email_destino) {
      toast.error("El expediente no tiene email de contacto");
      return;
    }
    if (!window.confirm(`¿Enviar los documentos a ${status.email_destino}?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/email-documentos-res022/${dossierId}/send`);
      toast.success("Documentos enviados por email");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el email");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const lastSend = status.envios_previos?.[status.envios_previos.length - 1];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Mail size={18} /> Enviar documentos por email</div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="enviar-documentos-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
        <CardDescription>
          Envía un ZIP con la documentación final al email del expediente{status.email_destino ? ` (${status.email_destino})` : ""}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="text-sm space-y-1">
          {status.documentos.map((d, i) => (
            <li key={i} className="flex items-center gap-2">
              {d.present ? <CheckCircle size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-red-500" />}
              <span className={d.present ? "" : "text-red-600"}>{d.label}{!d.present && " — falta"}</span>
            </li>
          ))}
        </ul>

        {!status.email_destino && (
          <p className="text-sm text-red-600">El expediente no tiene email de contacto — añádelo en la ficha del cliente.</p>
        )}
        {!status.puede_enviarse && (
          <p className="text-sm text-amber-600">
            No se puede enviar: faltan documentos obligatorios ({status.faltan_obligatorios.join(", ")}). El Acuerdo y el Anexo deben estar generados.
          </p>
        )}

        {lastSend && (
          <p className="text-xs text-slate-500">
            {lastSend.success ? "Último envío correcto" : "Último envío falló"} el {new Date(lastSend.sent_at).toLocaleString("es-ES")} a {lastSend.to}
          </p>
        )}

        <Button
          onClick={send}
          disabled={busy || !status.puede_enviarse || !status.email_destino}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="enviar-documentos-btn"
        >
          {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
          Enviar documentos
        </Button>
      </CardContent>
    </Card>
  );
};

export default EnviarDocumentosSection;
